import React, { useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import AlbumIcon from '../../Assets/Featuresection/album_logo.webp';
import UnlockIcon from '../../Assets/Featuresection/unlock_logo.webp';
import SubscribeIcon from '../../Assets/Featuresection/subscribe_logo.webp';
import ShareIcon from '../../Assets/Featuresection/share_logo.webp';
import FaceScanLogo from '../../Assets/Featuresection/facescan_logo.webp';
import PhoneFrame from '../../Assets/Featuresection/feature_phone.webp';

gsap.registerPlugin(ScrollTrigger);

const FEATURES = [
  {
    title: 'Album Delivery',
    desc: 'Upload and deliver beautiful client galleries in minutes, right from the app.',
    icon: AlbumIcon,
    bgColor: '#FCF5EC',
  },
  {
    title: 'Lock Albums on Payment',
    desc: `Full-res photos unlock automatically the moment the client's balance is cleared.`,
    icon: UnlockIcon,
    bgColor: '#F0F4FF',
  },
  {
    title: 'Smart Event Calendar',
    desc: 'See every booking, assign your crew, and never double-book a date again.',
    icon: SubscribeIcon,
    bgColor: '#FFF0F2',
  },
  {
    title: 'Event QR Code Sharing',
    desc: 'Share a QR code at events so guests can find and download their own photos instantly.',
    icon: ShareIcon,
    bgColor: '#EBF9FA',
  },
];

const NewFeatureSection = () => {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const areaRef = useRef(null);
  const phoneRef = useRef(null);
  const whiteCardRef = useRef(null);
  const cardsLayerRef = useRef(null);
  const slotRefs = useRef([]);
  const cardRefs = useRef([]);
  const mobileSectionRef = useRef(null);
  const mobileCardsRef = useRef([]);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    const pin = pinRef.current;
    const area = areaRef.current;
    const phone = phoneRef.current;
    const whiteCard = whiteCardRef.current;
    const cardsLayer = cardsLayerRef.current;
    const slots = slotRefs.current.filter(Boolean);
    const cards = cardRefs.current.filter(Boolean);
    const mobileSection = mobileSectionRef.current;
    const mobileCards = mobileCardsRef.current.filter(Boolean);

    if (!section || !pin || !area || !phone || !whiteCard || !cardsLayer || slots.length !== 4 || cards.length !== 4) {
      return undefined;
    }

    const mm = gsap.matchMedia();

    mm.add('(min-width: 1400px)', () => {
      const PHONE_START_X = 0;

      let currentSlotPositions = [];
      let currentWhiteCardWidth = 0;
      let currentWhiteCardHeight = 0;
      let currentStackX = 0;
      let currentStackY = 0;

      const measure = () => {
        gsap.set(phone, { x: PHONE_START_X });

        const layerRect = cardsLayer.getBoundingClientRect();
        const whiteRect = whiteCard.getBoundingClientRect();
        currentWhiteCardWidth = whiteRect.width;
        currentWhiteCardHeight = whiteRect.height;

        currentSlotPositions = slots.map((slot) => {
          const slotRect = slot.getBoundingClientRect();
          return {
            x: slotRect.left - layerRect.left,
            y: slotRect.top - layerRect.top,
            width: slotRect.width,
            height: slotRect.height,
          };
        });

        currentStackX = whiteRect.left - layerRect.left;
        currentStackY = whiteRect.top - layerRect.top;
      };

      const applyStack = () => {
        cards.forEach((card, index) => {
          const slot = currentSlotPositions[index];
          gsap.set(card, {
            x: currentStackX,
            y: currentStackY,
            width: slot?.width ?? currentWhiteCardWidth,
            height: slot?.height ?? currentWhiteCardHeight,
            scale: 1,
            opacity: 1,
            zIndex: 3 + index,
          });
        });
      };

      measure();
      applyStack();

      const tl = gsap.timeline({
  scrollTrigger: {
    trigger: section,
    start: 'top top',
    end: '+=180%',  
    pin: pin,
    pinSpacing: true,
    scrub: 1.2,    
    anticipatePin: 1,
    preventOverlaps: 'page-sections',
    fastScrollEnd: true,
    invalidateOnRefresh: true,
    onRefresh: () => {
      measure();
      applyStack();
    },
  },
});

cards.forEach((card, index) => {
  tl.to(
    card,
    {
      x: () => currentSlotPositions[index]?.x ?? 0,
      y: () => currentSlotPositions[index]?.y ?? 0,
      scale: 1,
      opacity: 1,
      duration: 2.15,    
      ease: 'power1.inOut', 
    },
    0.1 + index * 0.055
  );
});


tl.to({}, { duration: 0.5 });

      const refresh = () => ScrollTrigger.refresh();
      window.addEventListener('load', refresh);
      window.addEventListener('resize', refresh);

      return () => {
        window.removeEventListener('load', refresh);
        window.removeEventListener('resize', refresh);
        tl.scrollTrigger?.kill();
        tl.kill();
      };
    });

    mm.add('(max-width: 1399px)', () => {
      gsap.set([phone, ...cards], { clearProps: 'all' });

      if (!mobileSection || mobileCards.length !== 5) {
        return undefined;
      }

      gsap.set(mobileCards, {
        y: 60,
        scale: 0.95,
        autoAlpha: 0,
      });

      gsap.set(mobileCards[0], {
        y: 0,
        scale: 1,
        autoAlpha: 1,
      });

      mobileCards.forEach((card, index) => {
        gsap.set(card, { zIndex: 5 + index });
      });

      const mobileTl = gsap.timeline({
        scrollTrigger: {
          trigger: mobileSection,
          start: 'top top',
          end: '+=300%',
          pin: true,
          pinSpacing: true,
          scrub: 0.55,
          anticipatePin: 1,
          fastScrollEnd: true,
          invalidateOnRefresh: true,
        },
      });

      for (let index = 1; index < mobileCards.length; index += 1) {
        const previousIndex = index - 1;

        mobileTl.to(
          mobileCards[index],
          {
            y: 0,
            autoAlpha: 1,
            scale: 1,
            duration: 0.85,
            ease: 'power2.out',
          },
          (index - 1) * 1.2
        );

        mobileTl.to(
          mobileCards[previousIndex],
          {
            y: -60,
            autoAlpha: 0,
            scale: 0.95,
            duration: 0.85,
            ease: 'power2.out',
          },
          (index - 1) * 1.2
        );

        mobileTl.to({}, { duration: 0.35 });
      }

      return () => {
        mobileTl.scrollTrigger?.kill();
        mobileTl.kill();
      };
    });

    return () => mm.revert();
  }, []);

  return (
    <SectionWrapper ref={sectionRef} id="features">
      <PinStage ref={pinRef}>
        <SectionHeader>
          <Title>
            What You <TitleGradient>Can Do</TitleGradient>
          </Title>
          <Subtitle>FEATURES</Subtitle>
        </SectionHeader>

        <ContentGrid>
          <DesktopAnimationArea ref={areaRef}>
            <CardsFinalRow>
              {FEATURES.map((feat, index) => (
                <CardSlot
                  key={feat.title}
                  ref={(el) => {
                    slotRefs.current[index] = el;
                  }}
                  aria-hidden
                >
                  <Card bgColor={feat.bgColor} style={{ visibility: 'hidden' }}>
                    <IconWrapper>
                      <IconImg src={feat.icon} alt="" />
                    </IconWrapper>
                    <CardTitle>{feat.title}</CardTitle>
                    <CardDescription>{feat.desc}</CardDescription>
                  </Card>
                </CardSlot>
              ))}
            </CardsFinalRow>

            <PhoneTrack>
              <PhoneWrapper ref={phoneRef}>
                <PhoneImage src={PhoneFrame} alt="Feature phone mockup" />
                <AnimatedCardsLayer ref={cardsLayerRef}>
                  {FEATURES.map((feat, index) => (
                    <AnimatedCard
                      key={feat.title}
                      ref={(el) => {
                        cardRefs.current[index] = el;
                      }}
                      bgColor={feat.bgColor}
                    >
                      <IconWrapper>
                        <IconImg src={feat.icon} alt={feat.title} />
                      </IconWrapper>
                      <CardTitle>{feat.title}</CardTitle>
                      <CardDescription>{feat.desc}</CardDescription>
                    </AnimatedCard>
                  ))}
                </AnimatedCardsLayer>
                <PhoneInnerCard ref={whiteCardRef} bgColor="#ffffff">
                  <PhoneIconWrapper>
                    <PhoneIconImg src={FaceScanLogo} alt="Face scan logo" />
                  </PhoneIconWrapper>
                  <CardTitle>One-Tap Payments</CardTitle>
                  <CardDescription>
                    Generate payment links, collect advances, and track every pending balance — no chasing.
                  </CardDescription>
                </PhoneInnerCard>
              </PhoneWrapper>
            </PhoneTrack>
          </DesktopAnimationArea>

          <MobileGridFallback ref={mobileSectionRef}>
            <MobilePhoneWrap>
              <PhoneWrapper>
                <PhoneImage src={PhoneFrame} alt="Feature phone mockup" />
                <MobileCardsLayer>
                  {FEATURES.map((feat, index) => (
                    <MobileAnimatedCard
                      key={feat.title}
                      ref={(el) => {
                        mobileCardsRef.current[index] = el;
                      }}
                      bgColor={feat.bgColor}
                    >
                      <IconWrapper>
                        <IconImg src={feat.icon} alt={feat.title} />
                      </IconWrapper>
                      <CardTitle>{feat.title}</CardTitle>
                      <CardDescription>{feat.desc}</CardDescription>
                    </MobileAnimatedCard>
                  ))}

                  <MobileAnimatedCard
                    ref={(el) => {
                      mobileCardsRef.current[FEATURES.length] = el;
                    }}
                    bgColor="#ffffff"
                  >
                    <PhoneIconWrapper>
                      <PhoneIconImg src={FaceScanLogo} alt="Face scan logo" />
                    </PhoneIconWrapper>
                    <CardTitle>Your customers can scan albums to find their photos</CardTitle>
                    <CardDescription>
                      Instant Discovery: Use Face Scan Technology to Effortlessly Find Your Photos
                    </CardDescription>
                  </MobileAnimatedCard>
                </MobileCardsLayer>
              </PhoneWrapper>
            </MobilePhoneWrap>
          </MobileGridFallback>
        </ContentGrid>
      </PinStage>
    </SectionWrapper>
  );
};

export default NewFeatureSection;

const SectionWrapper = styled.section`
  background: #ffffff;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  isolation: isolate;
  z-index: 1;
`;

const PinStage = styled.div`
  min-height: 100vh;
  padding: 64px 64px 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    min-height: auto;
    padding: 48px 24px 64px;
  }

  @media (min-width: 1600px) {
    padding: 100px 100px 120px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 56px;
  flex-shrink: 0;

  @media (min-width: 1600px) {
    margin-bottom: 80px;
  }
`;

const Title = styled.h2`
  font-family: 'Playfair Display', serif;
  font-weight: 600;
  font-style: italic;
  font-size: 64px;
  color: #121212;
  margin: 0 0 12px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 34px;
    line-height: 1.08;
  }

  @media (min-width: 1600px) {
    font-size: 80px;
  }
`;

const TitleGradient = styled.span`
  background: linear-gradient(140.02deg, #ffc951 2.22%, #c94daa 96.12%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
`;

const Subtitle = styled.div`
  font-family: 'Gilroy-SemiBold', 'Poppins', sans-serif;
  font-size: 28px;
  font-weight: 500;
  line-height: 140%;
  text-align: center;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: linear-gradient(140.02deg, #ffc951 2.22%, #c94daa 96.12%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;

  &::before,
  &::after {
    content: '•';
    color: #000000;
    -webkit-text-fill-color: #000000;
  }

  @media (min-width: 1600px) {
    font-size: 34px;
  }
`;

const ContentGrid = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;

  @media (max-width: 1024px) {
    height: auto;
  }
`;

const DesktopAnimationArea = styled.div`
  position: relative;
  width: 100%;
  height: min(78vh, 720px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;

  @media (max-width: 1399px) {
    display: none;
  }
`;

const CardsFinalRow = styled.div`
  flex: 1;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  align-items: center;
  z-index: 1;
`;

const CardSlot = styled.div`
  min-width: 0;
`;

const AnimatedCardsLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  overflow: visible;
`;

const PhoneTrack = styled.div`
  flex-shrink: 0;
  width: 360px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  z-index: 4;

  @media (min-width: 1600px) {
    width: 400px;
  }
`;

const MobileGridFallback = styled.div`
  display: none;
  width: 100%;

  @media (max-width: 1399px) {
    display: block;
  }
`;

const MobilePhoneWrap = styled.div`
  display: flex;
  justify-content: center;
  min-height: 100vh;
  position: relative;

  @media (max-width: 768px) {
    min-height: 56vh;
    align-items: flex-start;
    padding-top: 10px;
    padding-bottom: 0;
  }
`;

const MobileCardsLayer = styled.div`
  position: absolute;
  top: 12%;
  left: 6%;
  width: 88%;
  height: 74%;
  z-index: 4;
  overflow: hidden;
`;

const Card = styled.div`
  background-color: ${(props) => props.bgColor};
  border-radius: 24px;
  padding: 28px 24px;
  height: 286px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    height: 252px;
    padding: 22px 18px;
  }

  @media (max-width: 900px) {
    height: auto;
    min-height: 190px;
    padding: 20px 16px;
  }

  @media (min-width: 1600px) {
    height: 360px;
    padding: 36px 32px;
    border-radius: 32px;
  }
`;

const AnimatedCard = styled(Card)`
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  will-change: transform, opacity;
  transform-origin: center center;
`;

const MobileAnimatedCard = styled(Card)`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto;
  width: 100%;
  min-height: 0;
  height: clamp(170px, 52%, 210px);
  box-shadow: none;
  border-radius: 20px;
  padding: 16px 14px;
  overflow: hidden;

  > div:first-child {
    width: 46px;
    height: 46px;
    min-width: 46px;
    min-height: 46px;
    margin-bottom: 18px;
    border-radius: 10px;
  }

  > div:first-child img {
    width: 22px;
    height: 22px;
  }

  > h3 {
    font-size: 18px;
    line-height: 1.1;
    margin-bottom: 10px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  > p {
    font-size: 11px;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  @media (max-width: 1024px) {
    height: clamp(164px, 50%, 198px);
    padding: 15px 13px;

    > h3 {
      font-size: 16px;
    }

    > p {
      font-size: 10px;
    }
  }

  @media (max-width: 768px) {
    height: clamp(154px, 49%, 188px);
    border-radius: 18px;
    padding: 14px 12px;

    > h3 {
      font-size: 14px;
      margin-bottom: 8px;
    }

    > p {
      font-size: 9px;
      line-height: 1.3;
    }
  }
`;

const IconWrapper = styled.div`
  width: 58px;
  height: 58px;
  min-width: 58px;
  min-height: 58px;
  background: #ffffff;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: none;
  margin-bottom: 64px;

  @media (max-width: 1200px) {
    margin-bottom: 44px;
  }

  @media (min-width: 1600px) {
    width: 72px;
    height: 72px;
    border-radius: 14px;
    margin-bottom: 80px;
  }
`;

const IconImg = styled.img`
  width: 26px;
  height: 26px;
  object-fit: contain;

  @media (min-width: 1600px) {
    width: 32px;
    height: 32px;
  }
`;

const CardTitle = styled.h3`
  font-family: 'Playfair Display', serif;
  font-size: 22px;
  font-weight: 600;
  line-height: 100%;
  color: #000000;
  margin: 0 0 12px;

  @media (max-width: 1200px) {
    font-size: 18px;
  }

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (min-width: 1600px) {
    font-size: 28px;
    margin-bottom: 16px;
  }
`;

const CardDescription = styled.p`
  font-family: 'Gilroy-Medium', 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: 135%;
  color: #000000;
  margin: 0;

  @media (min-width: 1600px) {
    font-size: 14px;
    line-height: 1.5;
  }

  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const PhoneWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 360px;
  aspect-ratio: 1 / 2.05;
  will-change: transform;

  @media (max-width: 1024px) {
    max-width: 320px;
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    max-width: 250px;
  }

  @media (min-width: 1600px) {
    max-width: 400px;
  }
`;

const PhoneImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
`;

const PhoneInnerCard = styled(Card)`
  position: absolute;
  z-index: 4;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 82%;
  min-height: 286px;
  box-shadow: none;

  @media (max-width: 768px) {
    width: 78%;
    min-height: 220px;
    padding: 18px 14px;
  }

  @media (min-width: 1600px) {
    min-height: 360px;
  }
`;

const PhoneIconWrapper = styled(IconWrapper)`
  background: #dff4c8;

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;
    margin-bottom: 38px;
  }
`;

const PhoneIconImg = styled(IconImg)`
  width: 34px;
  height: 34px;

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
`;
